import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import PageHero from "@/components/PageHero";
import Marquee from "@/components/Marquee";
import { FadeIn, ClipReveal, Eyebrow } from "@/components/Reveal";
import { useSiteContent } from "@/hooks/useSiteContent";
import { IMAGES, SERVICES, SOA, INNOVATION, MARQUEE_ITEMS } from "@/data/content";

export default function MissionPage() {
  const { content } = useSiteContent();
  return (
    <>
      <PageHero
        eyebrow="La nostra Mission"
        lines={["Sicurezza,", "Efficienza", <span key="d" className="text-brand-ice">Durabilità</span>]}
        description="Contribuiamo allo sviluppo del territorio garantendo sempre la massima qualità delle opere realizzate."
        image={IMAGES.slopePine}
      />

      <section className="py-24 lg:py-32" data-testid="mission-quote">
        <div className="mx-auto w-full max-w-5xl px-5 sm:px-8">
          <FadeIn>
            <blockquote className="border-l-2 border-brand-cyan pl-8 sm:pl-12" data-testid="mission-quote-text">
              <p className="font-display text-3xl font-semibold uppercase leading-[1.1] tracking-tight text-white sm:text-5xl">
                {content.mission_quote}
              </p>
            </blockquote>
          </FadeIn>

          <div className="mt-16 grid gap-6 md:grid-cols-3" data-testid="mission-pillars">
            {[
              { t: "Sicure", d: "Ogni intervento segue protocolli rigorosi, dal cantiere alla consegna." },
              { t: "Efficienti", d: "Tecnologie e metodologie aggiornate per tempi certi e ottimizzati." },
              { t: "Duraturi", d: "Opere pensate per resistere nel tempo e proteggere il territorio." },
            ].map((p, i) => (
              <FadeIn key={p.t} delay={0.08 * i}>
                <div className="h-full border border-white/10 bg-brand-card p-8 transition-colors hover:border-brand-cyan/50">
                  <p className="font-mono text-xs text-brand-cyan">0{i + 1}</p>
                  <h3 className="mt-5 font-display text-2xl font-bold uppercase tracking-tight text-white">{p.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.d}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-brand-teal py-24 lg:py-32" data-testid="mission-activities">
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-8">
          <Eyebrow>Cosa facciamo</Eyebrow>
          <FadeIn delay={0.05}>
            <h2 className="mt-6 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
              Attività e servizi
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Operiamo nel settore dell'edilizia con particolare specializzazione in:
            </p>
          </FadeIn>
          <div className="mt-14">
            {SERVICES.map((s, i) => (
              <FadeIn key={s.id} delay={0.05 * i} amount={0.3}>
                <Link
                  to="/attivita-e-servizi"
                  data-testid={`mission-activity-${s.id}`}
                  className="group flex items-center justify-between gap-6 border-t border-white/10 py-7 transition-colors last:border-b hover:bg-white/[0.03]"
                >
                  <div className="flex items-baseline gap-6 sm:gap-10">
                    <span className="font-mono text-xs text-brand-cyan">0{i + 1}</span>
                    <span className="font-display text-2xl font-bold uppercase tracking-tight text-white transition-colors group-hover:text-brand-ice sm:text-4xl">
                      {s.fullTitle}
                    </span>
                  </div>
                  <ArrowRight className="h-5 w-5 shrink-0 text-white/30 transition-all duration-300 group-hover:translate-x-2 group-hover:text-brand-ice" />
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="mission-innovation">
        <div className="mx-auto grid w-full max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
          <div>
            <Eyebrow>{INNOVATION.subtitle}</Eyebrow>
            <FadeIn delay={0.05}>
              <h2 className="mt-6 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
                {INNOVATION.title}
              </h2>
            </FadeIn>
            <FadeIn delay={0.15}>
              <p className="mt-8 text-base leading-relaxed text-slate-400 sm:text-lg">
                {content.innovation_text}
              </p>
            </FadeIn>
          </div>
          <ClipReveal>
            <div className="relative overflow-hidden">
              <img src={IMAGES.ropeAction} alt="Interventi in cordata su parete rocciosa" className="aspect-[4/5] w-full object-cover" />
              <div className="absolute inset-0 border border-white/10" />
            </div>
          </ClipReveal>
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#05090D] py-24 lg:py-28" data-testid="mission-certifications">
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-8">
          <div className="flex items-center gap-4">
            <ShieldCheck className="h-6 w-6 text-brand-gold" />
            <FadeIn delay={0}>
              <h2 className="font-display text-3xl font-bold uppercase tracking-tight sm:text-5xl">
                Certificata <span className="text-brand-ice">UNI EN ISO 9001</span>
              </h2>
            </FadeIn>
          </div>
          <FadeIn delay={0.1}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">
              Attestazione SOA per le categorie:
            </p>
          </FadeIn>
          <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {SOA.map((c, i) => (
              <FadeIn key={c.code} delay={0.04 * i} amount={0.3}>
                <div
                  data-testid={`soa-card-${c.code.replace(/\s/g, "").toLowerCase()}`}
                  className="flex h-full flex-col justify-between bg-brand-slate p-7"
                >
                  <motion.p
                    className="font-mono text-lg font-semibold uppercase tracking-[0.1em] text-brand-gold"
                    whileHover={{ scale: 1.04 }}
                  >
                    {c.code} {c.level}
                  </motion.p>
                  <p className="mt-5 text-sm leading-snug text-slate-400">{c.title}</p>
                </div>
              </FadeIn>
            ))}
            <div className="hidden bg-brand-slate lg:block" aria-hidden="true" />
            <div className="hidden bg-brand-slate lg:block" aria-hidden="true" />
          </div>
          <FadeIn delay={0.2}>
            <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.3em] text-white/35">
              SOA — Attestazione di qualificazione per l'esecuzione di lavori pubblici
            </p>
          </FadeIn>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} className="border-t-0" />
    </>
  );
}
